/** @jsxImportSource @emotion/react */
import {
  ColumnDef,
  ExpandedState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { FolderData } from '../../../@types/api';
import { ReactComponent as SortAscIcon } from '../../../assets/images/ic_sort_asc.svg';
import { ReactComponent as SortDefaultIcon } from '../../../assets/images/ic_sort_default.svg';
import { ReactComponent as SortDescIcon } from '../../../assets/images/ic_sort_desc.svg';
import { COLORS } from '../../../styles/colors';
import { FONT_WEIGHT } from '../../../styles/fonts';

type TableProps = {
  columns: ColumnDef<FolderData | undefined>[];
  data: (FolderData | undefined)[];
  setFolderId: (rowFolderId: number) => void;
  setFolderName: (rowFolderName: string | undefined) => void;
  setShowFileBox: (flag: boolean) => void;
  showFileBox: boolean;
};

const TableTree = ({
  columns,
  data,
  setFolderId,
  setFolderName,
  setShowFileBox,
  //   showFileBox,
}: TableProps): JSX.Element => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  //   const tableData = useMemo(() => convertToTableData(data), []);
  const toggle = (
    isExpanded: boolean,
    rowId: number,
    name: string | undefined,
  ) => {
    !isExpanded ? setShowFileBox(true) : setShowFileBox(false);
    !isExpanded ? setFolderId(rowId) : setFolderId(0);
    !isExpanded ? setFolderName(name) : setFolderName('');
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      sorting,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows: (row) => row?.subFolder,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <table
      style={{
        borderRadius: '0.625rem',
        fontSize: '2rem',
        borderTop: `0.0625rem solid ${COLORS.grey5}`,
        borderRight: `0.0625rem solid ${COLORS.grey5}`,
        borderLeft: `0.0625rem solid ${COLORS.grey5}`,
        // overflowX: 'scroll',
        borderCollapse: 'collapse',
        display: 'block',
        border: '0',
        borderSpacing: '0',
        cursor: 'default',
        width: '100%',
      }}
    >
      <thead
        style={{
          position: 'relative',
          top: 0,
          width: '100%',
          zIndex: 5,
          display: 'table',
          tableLayout: 'fixed',
        }}
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            style={{
              height: '3.75rem',
              lineHeight: '3.125rem',
            }}
          >
            {headerGroup.headers.map((header) => {
              return (
                <th
                  key={header.id}
                  style={{
                    // width: 'calc(100vh - 360px)',
                    width: header.column.columnDef.size,
                    letterSpacing: '0.019rem',
                    paddingLeft: '0.5rem',
                    color: COLORS.black2,
                    fontSize: '0.875rem',
                    fontWeight: FONT_WEIGHT.regular,
                    //padding: '0.1rem',
                    overflow: 'hidden',
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      style={{
                        letterSpacing: '0.019rem',
                      }}
                      role="presentation"
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      <span
                        style={{
                          padding: '0 0.5rem',
                          justifyContent: 'space-between',
                        }}
                      >
                        {{
                          asc: <SortAscIcon />,
                          desc: <SortDescIcon />,
                        }[header.column.getIsSorted() as string] ??
                          (header.column.getCanSort() ? (
                            <SortDefaultIcon />
                          ) : (
                            ''
                          ))}
                      </span>
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody
        style={{
          height: 'calc(100vh - 350px)',
          width: '100%',
          tableLayout: 'fixed',
          display: 'block',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {table.getRowModel().rows.map((row) => {
          return (
            <tr
              key={row.id}
              onClick={() =>
                row.original?.type === 'folder'
                  ? toggle(
                      row.getIsExpanded(),
                      Number(row.original?.id),
                      row.original?.path,
                    )
                  : null
              }
              // onDoubleClick={() => {
              //   window.navigator.clipboard.writeText(
              //     `S:\\${row.original?.path}` || '',
              //   );
              // }}
              style={{
                fontSize: '14px',
                color: COLORS.black2,
                // display: 'inline-block !important',
                display: 'table',
                tableLayout: 'fixed',
                width: '100%',
                letterSpacing: '0.019rem',
                borderBottom: `0.0625rem solid ${COLORS.grey10}`,
              }}
            >
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    style={{
                      overflowWrap: 'break-word',
                      padding: '0.5rem 0.4rem 0.5rem 0.5rem',
                      letterSpacing: '0.019rem',
                      minWidth: cell.column.columnDef.minSize,
                      width: cell.column.columnDef.size,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default TableTree;
